/**
 * The alphabet, as a reference ([Review](../../../docs/REVIEW.md#letters),
 * [D-035](../../../docs/DECISIONS.md#d-035), [D-137](../../../docs/DECISIONS.md#d-137),
 * mockup 32).
 *
 * **Two sounds, not one.** A letter has a *name*, for spelling out loud ("zed",
 * "double-you"), and a *sound*, for reading words ("w" as in water). Both are
 * new to a Danish speaker, and teaching only one leaves a child stuck at the
 * other, so they are on separate buttons.
 *
 * The letters themselves are content the app carries
 * ([`letters.json`](../../../content/en-da/letters.json)). The one thing here that
 * is this child's is the ⭐ on a letter whose sound they are practising, which
 * comes from the server and is simply absent if it cannot be fetched.
 */

import '@capytalk/tokens/blocks/letters.css';
import { type Letter, lettersFor } from '@capytalk/content/letters';
import { learningNow } from './language.js';
import { useEffect, useRef, useState } from 'react';
import type { Client } from './api.js';
import { hush, say } from './audio.js';
import Sneakers from './Sneakers.js';

/** Time between letters when the whole alphabet is played: a pace to sing to. */
export const ALPHABET_STEP_MS = 900;

const nameSaid = (letter: Letter): string => `${letter.letter.toUpperCase()}.`;

function LetterSheet({
  letter,
  practising,
  onClose,
}: {
  readonly letter: Letter;
  readonly practising: boolean;
  readonly onClose: () => void;
}) {
  useEffect(() => {
    const key = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    globalThis.addEventListener('keydown', key);
    return () => {
      globalThis.removeEventListener('keydown', key);
    };
  }, [onClose]);

  const upper = letter.letter.toUpperCase();
  return (
    <>
      <button
        className="dim"
        type="button"
        aria-hidden="true"
        tabIndex={-1}
        style={{ border: 0, padding: 0 }}
        onClick={onClose}
      />
      <div
        className="pop"
        role="dialog"
        aria-modal="true"
        aria-label={`The letter ${upper}`}
        style={{
          top: 'auto',
          bottom: 0,
          left: 0,
          right: 0,
          borderRadius: '24px 24px 0 0',
          padding: '20px 20px 34px',
        }}
      >
        <div className="between">
          <div className="row" style={{ gap: 12 }}>
            <div
              style={{ fontSize: 44, fontWeight: 800, lineHeight: 1 }}
              aria-hidden="true"
            >
              {upper}
              {letter.letter}
            </div>
            {practising && (
              <span
                className="chip"
                style={{ borderColor: 'var(--gold)', background: '#FFFBF0' }}
              >
                ⭐ practising
              </span>
            )}
          </div>
          <button
            className="x"
            type="button"
            aria-label="Close"
            style={{ background: 'none', border: 0, font: 'inherit' }}
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className="card" style={{ marginTop: 14, background: 'var(--bg)' }}>
          <button
            type="button"
            className="lrow"
            style={{
              width: '100%',
              background: 'none',
              border: 0,
              borderBottom: undefined,
              textAlign: 'left',
              font: 'inherit',
              color: 'inherit',
            }}
            aria-label={`Hear its name: ${letter.name}`}
            onClick={() => {
              void say(letter.nameAudioRef, nameSaid(letter));
            }}
          >
            <span className="ic" aria-hidden="true">
              🔤
            </span>
            <span className="tx">
              <span className="kicker" style={{ display: 'block' }}>
                ITS NAME — for spelling out loud
              </span>
              <span
                style={{
                  display: 'block',
                  fontSize: '1.2rem',
                  fontWeight: 800,
                  marginTop: 2,
                }}
              >
                “{letter.name}”
              </span>
            </span>
            <span aria-hidden="true">🔊</span>
          </button>
          {letter.sounds.map((sound, at) => (
            <button
              key={sound.word}
              type="button"
              className="lrow"
              style={{
                width: '100%',
                background: 'none',
                border: 0,
                borderBottom: at === letter.sounds.length - 1 ? 0 : undefined,
                textAlign: 'left',
                font: 'inherit',
                color: 'inherit',
              }}
              aria-label={`Hear its sound: ${sound.sound} as in ${sound.word}`}
              onClick={() => {
                void say(sound.audioRef, sound.word, { slow: true });
              }}
            >
              <span className="ic" aria-hidden="true">
                {sound.emoji}
              </span>
              <span className="tx">
                <span className="kicker" style={{ display: 'block' }}>
                  {at === 0 ? 'ITS SOUND — for reading words' : 'IT CAN ALSO SAY'}
                </span>
                <span
                  style={{
                    display: 'block',
                    fontSize: '1.2rem',
                    fontWeight: 800,
                    marginTop: 2,
                  }}
                >
                  “{sound.sound}” as in <b>{sound.word}</b>
                </span>
              </span>
              <span aria-hidden="true">🔊</span>
            </button>
          ))}
        </div>

        {practising && (
          <p
            className="tiny"
            style={{ marginTop: 14, textAlign: 'center', fontWeight: 700 }}
          >
            Sneakers noticed this one is tricky for you, so you are practising it
            together.
          </p>
        )}
      </div>
    </>
  );
}

export default function Letters({ client }: { readonly client: Client }) {
  const [practising, setPractising] = useState<ReadonlySet<string>>(new Set());
  const [open, setOpen] = useState<Letter | null>(null);
  const [singing, setSinging] = useState<number | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let live = true;
    // Through `Promise.resolve` because an older server has no such procedure
    // and the call throws rather than rejecting (D-066). The marker is a nicety:
    // without it the alphabet is still the whole alphabet.
    Promise.resolve()
      .then(() => client.learner.practisingLetters.query())
      .then((letters) => {
        if (live) setPractising(new Set(letters));
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, [client]);

  const stop = () => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
    setSinging(null);
    hush();
  };

  useEffect(
    () => () => {
      if (timer.current !== null) clearTimeout(timer.current);
    },
    [],
  );

  const sing = (at: number) => {
    const letter = lettersFor(learningNow().l2)[at];
    if (letter === undefined) {
      timer.current = null;
      setSinging(null);
      return;
    }
    setSinging(at);
    void say(letter.nameAudioRef, nameSaid(letter));
    timer.current = setTimeout(() => {
      sing(at + 1);
    }, ALPHABET_STEP_MS);
  };

  return (
    <div className="grow pad" style={{ overflowY: 'auto' }}>
      <Sneakers text="Tap a letter and I will say it!" audioRef={null} size={46} />

      <div className="row" style={{ gap: 8, margin: '12px 0', flexWrap: 'wrap' }}>
        <button
          type="button"
          className={singing === null ? 'chip' : 'chip on'}
          aria-pressed={singing !== null}
          onClick={() => {
            if (singing === null) sing(0);
            else stop();
          }}
        >
          <span aria-hidden="true">🔊</span>{' '}
          {singing === null ? 'Hear the whole alphabet' : 'Stop'}
        </button>
        {practising.size > 0 && (
          <span
            className="chip"
            style={{ borderColor: 'var(--gold)', background: '#FFFBF0' }}
          >
            ⭐ practising
          </span>
        )}
      </div>

      <div className="alpha">
        {lettersFor(learningNow().l2).map((letter, at) => {
          const marked = practising.has(letter.letter);
          const lit = open?.letter === letter.letter || singing === at;
          return (
            <button
              key={letter.letter}
              type="button"
              className={lit ? 'ltr sel' : marked ? 'ltr prac' : 'ltr'}
              style={{ font: 'inherit', color: 'inherit', cursor: 'pointer' }}
              aria-label={`${letter.letter.toUpperCase()}, ${letter.name}${marked ? ', practising' : ''}`}
              onClick={() => {
                stop();
                setOpen(letter);
                void say(letter.nameAudioRef, nameSaid(letter));
              }}
            >
              <div className="u">
                {letter.letter.toUpperCase()}
                {letter.letter}
              </div>
              <div className="n">{letter.name}</div>
            </button>
          );
        })}
      </div>

      {open !== null && (
        <LetterSheet
          letter={open}
          practising={practising.has(open.letter)}
          onClose={() => {
            setOpen(null);
          }}
        />
      )}
    </div>
  );
}
